import dayjs from 'dayjs'
import { TrackedChangeType, TrackedChange, schema } from 'prose-core'
import { Fragment, type Node as ProseNode } from 'prosemirror-model'
import { Selection } from 'prosemirror-state'

import { ourUuid } from 'utils'

import { addDeletionChange, addDeletionMarks } from '../addDeletionMarks'
import { getDeletionAttrs, isDeletion } from '../utils'
import { CommandTrackerProps, type TrackedChangesCommandHandler } from './types'
import { MatchingNode, fastHeuristicSearch } from './utils'

export const pullThroughDeleted: TrackedChangesCommandHandler = (props): boolean => {
  const { wrappedTr, state, userId } = props
  const createdAt = dayjs().toISOString()

  if (!isEmptySelection(state.selection)) {
    return false
  }

  const { status, match } = fastHeuristicSearch(wrappedTr.doc.content, ({ node, from, to }) => {
    // This node ends before the selection, fast forward
    if (to < state.selection.to + 1) {
      return 'skip'
    }
    // This node starts before the selection, search
    // Unless it is a text node, in which case we can work its content
    if (!node.isText && from < state.selection.from) {
      return 'search'
    }
    // We found a good match
    if (!isDeletion(node)) {
      return 'found'
    }
    // Everything ahead of the cursor is a possibly valid match, search
    return 'search'
  })

  if (status === 'found' && match.node.isText) {
    // If the selection is in the middle of the word, this does it
    const from = Math.max(state.selection.from, match.from)
    // Search for a sibling deletion
    // TODO: fix types
    // let lastDeletion: {node: ProseNode, from: number, attrs: Record<string, string>} | null = null;
    let lastDeletion: any = null
    const parent = match.parentList.at(-1)
    // Try and reuse the parents deletion uuid
    const parentDeletionAttrs = parent && isDeletion(parent.node) ? getDeletionAttrs(parent.node) : []
    const uuid = parentDeletionAttrs.length > 0 ? parentDeletionAttrs[0].uuid : ourUuid()
    // Not the start of the node, search for a sibling deletion
    if (state.selection.from < match.from && parent != null) {
      const localFrom = from - parent.from - 1
      parent.node.content.forEach((node, offset) => {
        if (offset < localFrom && isDeletion(node)) {
          const attrs = getDeletionAttrs(node)
          if (attrs.length > 0) {
            lastDeletion = { node, from: offset + parent.from + 1, attrs: attrs[0] }
          }
        }
      })
    }
    if (lastDeletion != null) {
      // Expand neighboring deletion
      wrappedTr.addMark(lastDeletion.from, from + 1, schema.marks.deletion.create(lastDeletion.attrs))
    } else {
      // New deletion
      wrappedTr.addMark(from, from + 1, schema.marks.deletion.create({ userId, createdAt, uuid }))
    }
    return true
  }

  if (status === 'found') {
    const nextNode = match.node

    // We actually need to pull a column out of a group, so we need to do a replace step
    if (nextNode.type.name === 'column_group') {
      return processColumnGroup(match, props)
    }

    // Likely a group with a column already deleted, we could attempt to reuse some code but the positions would be way off
    if (nextNode.type.name === 'column') {
      return processColumn(match, props)
    }

    // TODO: I really want to remove the insertions, but this doesn't work
    // if (isInsertion(nextNode)) {
    //   console.log(`pullPastDeleted - insertion - deleting for real`);
    //   wrappedTr.delete(pastDeleted.from-1, pastDeleted.to-1)
    //   return true
    // }

    // TODO: There was a shorthand somewhere
    const trackedChanges = (nextNode.attrs.trackedChanges || [])
      .filter(({ type }: TrackedChange) => ![TrackedChangeType.Insertion, TrackedChangeType.Deletion].includes(type))
      .concat({ type: TrackedChangeType.Deletion, userId, createdAt: dayjs().toISOString(), uuid: ourUuid() })

    wrappedTr.setNodeMarkup(match.from, nextNode.type, { ...nextNode.attrs, trackedChanges }, nextNode.marks)
    return true
  }

  // Although we could not process it, we want to silence the default behavior
  return true
}

function processColumnGroup(
  { node: nextNode, from }: MatchingNode,
  { wrappedTr, userId }: CommandTrackerProps,
): boolean {
  const uuid = ourUuid()
  const createdAt = dayjs().toISOString()

  // Mark the group as deleted
  addDeletionChange(wrappedTr, nextNode, from, { type: TrackedChangeType.Deletion, userId, createdAt, uuid })

  // If it has more than a single column, pick the first one, tag it as deleted, clone it's content and insert before the group
  if (nextNode.childCount > 1) {
    const column = nextNode.child(0)
    // Mark the column as deleted
    addDeletionChange(wrappedTr, nextNode, from + 1, { type: TrackedChangeType.Deletion, userId, createdAt, uuid })

    // Mark the column content as deleted
    addDeletionMarks(wrappedTr, from + 1, from + 1 + column.nodeSize, userId)

    // Clone the columns content
    column.content.forEach((node: ProseNode) => {
      // What if this was already an insertion?
      const clonedNode = node.type.createAndFill(
        { ...node.attrs, trackedChanges: [{ type: TrackedChangeType.Insertion, userId, createdAt, uuid }] },
        node.content,
        node.marks,
      )
      if (clonedNode) {
        wrappedTr.insert(from - 1, Fragment.from(clonedNode))
      }
    })

    return true
  }

  return false
}

function processColumn(
  { node: columnNode, from, parentList }: MatchingNode,
  { wrappedTr, userId }: CommandTrackerProps,
): boolean {
  const uuid = ourUuid()
  const createdAt = dayjs().toISOString()

  // We would not get here if the group were not deleted

  // Mark the column as deleted
  addDeletionChange(wrappedTr, columnNode, from, { type: TrackedChangeType.Deletion, userId, createdAt, uuid })

  // Mark the column content as deleted
  addDeletionMarks(wrappedTr, from, from + columnNode.nodeSize, userId)

  // ColumnGroup
  const columnGroup = parentList.at(-1)

  if (columnGroup == null) {
    return true
  }

  // Clone the columns content
  columnNode.content.forEach((node: ProseNode) => {
    // What if this was already an insertion?
    const clonedNode = node.type.createAndFill(
      { ...node.attrs, trackedChanges: [{ type: TrackedChangeType.Insertion, userId, createdAt, uuid }] },
      node.content,
      node.marks,
    )
    if (clonedNode) {
      wrappedTr.insert(columnGroup.from, Fragment.from(clonedNode))
    }
  })

  return true
}

function isEmptySelection(selection: Selection) {
  return selection.empty && selection.$from.sameParent(selection.$to)
}
